import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Car, CheckCircle2, AlertTriangle, ShieldAlert, ClipboardCheck, Flag } from "lucide-react";
import { toast } from "sonner";
import { STATUS_META, REPORT_STATUS, CATEGORY_LABEL, fmtDT, PhotoPicker } from "./shared";

const draftKey = (vid, kind) => `vc-draft-${vid}-${kind}`;
const newClientId = () => `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

function ItemRow({ def, ans, vehicleId, onChange }) {
  return (
    <div className="rounded-xl border border-slate-800 bg-black/30 p-3" data-testid={`vc-item-${def.key}`}>
      <div className="text-white text-sm font-semibold">{def.label}</div>
      {def.hint && <div className="text-xs text-slate-500">{def.hint}</div>}
      <div className="grid grid-cols-3 gap-2 mt-2">
        {Object.entries(STATUS_META).map(([k, m]) => (
          <button key={k} type="button" onClick={() => onChange({ ...ans, status: k })} data-testid={`vc-item-${def.key}-${k}`}
            className={`min-h-[44px] rounded-lg border text-xs font-semibold transition-colors ${ans.status === k ? m.cls : "border-slate-800 text-slate-500"}`}>{m.label}</button>
        ))}
      </div>
      {(ans.status === "anomalia" || ans.status === "non_verificato") && (
        <div className="mt-2 space-y-2">
          <textarea value={ans.note || ""} onChange={e => onChange({ ...ans, note: e.target.value })} rows={2} data-testid={`vc-item-${def.key}-note`}
            placeholder={ans.status === "anomalia" ? "Descrivi l'anomalia (obbligatorio)" : "Motivo (facoltativo)"}
            className="w-full rounded-lg bg-slate-950 border border-slate-800 text-sm text-slate-200 p-2" />
          {ans.status === "anomalia" && <PhotoPicker vehicleId={vehicleId} value={ans.photo_ids || []} testid={`vc-item-${def.key}-photos`}
            onChange={fn => onChange(a => ({ ...a, photo_ids: fn(a.photo_ids || []) }))} />}
        </div>
      )}
    </div>
  );
}

function CheckDialog({ vehicle, kind, items, onClose, onDone }) {
  const key = draftKey(vehicle.id, kind);
  const [form, setForm] = useState(() => {
    try { const d = JSON.parse(localStorage.getItem(key)); if (d) return d; } catch { /* bozza illeggibile */ }
    return { answers: {}, photo_ids: [], notes: "", client_id: newClientId() };
  });
  const [busy, setBusy] = useState(false);
  useEffect(() => { localStorage.setItem(key, JSON.stringify(form)); }, [form, key]);
  const done = items.filter(i => form.answers[i.key]?.status).length;
  const setAns = (k, v) => setForm(f => ({ ...f, answers: { ...f.answers, [k]: typeof v === "function" ? v(f.answers[k] || {}) : v } }));
  const submit = async () => {
    const missing = items.filter(i => !form.answers[i.key]?.status);
    if (missing.length) { toast.error(`Checklist incompleta: ${missing.map(m => m.label).join(", ")}`); return; }
    const noNote = items.find(i => form.answers[i.key].status === "anomalia" && !(form.answers[i.key].note || "").trim());
    if (noNote) { toast.error(`Descrivi l'anomalia: ${noNote.label}`); return; }
    setBusy(true);
    try {
      const r = await api.post("/vehicle-checks/me/checks", {
        vehicle_id: vehicle.id, kind, client_id: form.client_id, notes: form.notes, photo_ids: form.photo_ids,
        items: items.map(i => ({ key: i.key, ...form.answers[i.key] })),
      }, { timeout: 60000 });
      localStorage.removeItem(key);
      toast.success(r.data.anomalies ? `Controllo registrato con ${r.data.anomalies} anomalie segnalate` : "Controllo registrato: nessuna anomalia");
      onDone(r.data);
    } catch (e) {
      toast.error(`${formatApiError(e)} — i dati restano salvati su questo dispositivo, puoi riprovare.`);
    }
    setBusy(false);
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-xl w-[100vw] sm:w-auto h-[100dvh] sm:h-auto sm:max-h-[90vh] overflow-y-auto bg-[#0A0907] border-amber-500/30 p-4" data-testid="vc-check-dialog">
        <DialogHeader>
          <DialogTitle className="text-white font-display">{kind === "start" ? "Controllo inizio turno" : "Controllo fine turno"}</DialogTitle>
          <div className="text-xs text-slate-400">{vehicle.name} · {vehicle.plate} · {done}/{items.length} verificati</div>
        </DialogHeader>
        <div className="h-1.5 rounded-full bg-slate-800 overflow-hidden"><div className="h-full bg-gradient-to-r from-[#F1D27A] to-[#C99A24] transition-[width]" style={{ width: `${(done / items.length) * 100}%` }} /></div>
        <div className="space-y-2">
          {items.map(d => <ItemRow key={d.key} def={d} ans={form.answers[d.key] || {}} vehicleId={vehicle.id} onChange={v => setAns(d.key, v)} />)}
        </div>
        <div className="space-y-2">
          <div className="text-xs uppercase tracking-widest text-amber-400/80">Foto del veicolo</div>
          <PhotoPicker vehicleId={vehicle.id} value={form.photo_ids} onChange={fn => setForm(f => ({ ...f, photo_ids: fn(f.photo_ids) }))} testid="vc-check-photos" />
          <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} rows={2} placeholder="Note generali (facoltative)"
            className="w-full rounded-lg bg-slate-950 border border-slate-800 text-sm text-slate-200 p-2" data-testid="vc-check-notes" />
        </div>
        <p className="text-[11px] text-slate-500">La checklist digitale registra quanto verificato e non certifica la sicurezza meccanica del mezzo.</p>
        <button type="button" disabled={busy} onClick={submit} data-testid="vc-check-submit"
          className="w-full min-h-[52px] rounded-xl font-bold text-black bg-gradient-to-b from-[#F1D27A] to-[#C99A24] disabled:opacity-60">{busy ? "Invio in corso..." : "Completa controllo"}</button>
      </DialogContent>
    </Dialog>
  );
}

function ReportDialog({ vehicle, onClose, onDone }) {
  const key = `vc-report-draft-${vehicle.id}`;
  const [f, setF] = useState(() => {
    try { const d = JSON.parse(localStorage.getItem(key)); if (d) return d; } catch { /* bozza illeggibile */ }
    return { category: "", description: "", photo_ids: [], safety_critical: false, client_id: newClientId() };
  });
  const [busy, setBusy] = useState(false);
  useEffect(() => { localStorage.setItem(key, JSON.stringify(f)); }, [f, key]);
  const submit = async () => {
    if (!f.category || f.description.trim().length < 3) { toast.error("Scegli la categoria e descrivi il problema"); return; }
    setBusy(true);
    try {
      await api.post("/vehicle-checks/me/reports", { vehicle_id: vehicle.id, ...f }, { timeout: 60000 });
      localStorage.removeItem(key);
      toast.success("Segnalazione inviata all'ufficio");
      onDone();
    } catch (e) { toast.error(`${formatApiError(e)} — la segnalazione resta salvata, puoi riprovare.`); }
    setBusy(false);
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg bg-[#0A0907] border-amber-500/30" data-testid="vc-report-dialog">
        <DialogHeader><DialogTitle className="text-white font-display">Segnala anomalia · {vehicle.plate}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-2">
          {Object.entries(CATEGORY_LABEL).map(([k, l]) => (
            <button key={k} type="button" onClick={() => setF(x => ({ ...x, category: k }))} data-testid={`vc-report-cat-${k}`}
              className={`min-h-[44px] rounded-lg border text-xs font-semibold px-2 ${f.category === k ? "border-amber-400 text-amber-200 bg-amber-500/10" : "border-slate-800 text-slate-400"}`}>{l}</button>
          ))}
        </div>
        <textarea value={f.description} onChange={e => setF(x => ({ ...x, description: e.target.value }))} rows={3} placeholder="Descrizione del problema"
          className="w-full rounded-lg bg-slate-950 border border-slate-800 text-sm text-slate-200 p-2" data-testid="vc-report-desc" />
        <PhotoPicker vehicleId={vehicle.id} value={f.photo_ids} onChange={fn => setF(x => ({ ...x, photo_ids: fn(x.photo_ids) }))} testid="vc-report-photos" />
        {f.category !== "sicurezza" && (
          <label className="flex items-center gap-2 text-sm text-slate-300"><input type="checkbox" checked={f.safety_critical} onChange={e => setF(x => ({ ...x, safety_critical: e.target.checked }))} data-testid="vc-report-safety" />Può compromettere la sicurezza del mezzo</label>
        )}
        <button type="button" disabled={busy} onClick={submit} data-testid="vc-report-submit"
          className="w-full min-h-[50px] rounded-xl font-bold text-black bg-gradient-to-b from-[#F1D27A] to-[#C99A24] disabled:opacity-60">{busy ? "Invio..." : "Invia segnalazione"}</button>
      </DialogContent>
    </Dialog>
  );
}

function VehicleStatus({ v, today }) {
  if (v.blocked) return (
    <div className="rounded-xl border border-rose-500/60 bg-rose-500/10 p-3 flex gap-3 items-start" data-testid="vc-status-blocked">
      <ShieldAlert className="text-rose-400 shrink-0" />
      <div><div className="text-rose-200 font-bold">Veicolo da valutare</div><div className="text-xs text-rose-200/80">Segnalato un possibile problema di sicurezza: attendi la valutazione del responsabile prima di usarlo.</div></div>
    </div>
  );
  const lc = v.last_check;
  const ok = lc && lc.service_date === today && !lc.anomalies;
  return (
    <div className={`rounded-xl border p-3 flex gap-3 items-center ${ok ? "border-emerald-500/40 bg-emerald-500/5" : "border-amber-500/30 bg-amber-500/5"}`} data-testid="vc-status">
      {ok ? <CheckCircle2 className="text-emerald-400" /> : <AlertTriangle className="text-amber-400" />}
      <div>
        <div className={`font-bold ${ok ? "text-emerald-300" : "text-amber-200"}`}>{ok ? "Veicolo controllato oggi" : lc ? "Controllo da effettuare" : "Nessun controllo registrato"}</div>
        <div className="text-xs text-slate-400">{lc ? `Ultimo: ${lc.kind === "start" ? "inizio" : "fine"} turno ${fmtDT(lc.created_at)}${lc.anomalies ? ` · ${lc.anomalies} anomalie` : ""}` : "Esegui il controllo di inizio turno"}{v.open_reports ? ` · ${v.open_reports} segnalazioni aperte` : ""}</div>
      </div>
    </div>
  );
}

export function DriverVehicleCheck() {
  const [data, setData] = useState(null);
  const [vid, setVid] = useState(null);
  const [checks, setChecks] = useState([]);
  const [reports, setReports] = useState([]);
  const [dialog, setDialog] = useState(null);
  const load = async () => {
    try {
      const [a, b, c] = await Promise.all([api.get("/vehicle-checks/me/vehicles"), api.get("/vehicle-checks/me/checks"), api.get("/vehicle-checks/me/reports")]);
      setData(a.data); setChecks(b.data); setReports(c.data);
      setVid(v => v && a.data.vehicles.some(x => x.id === v) ? v : a.data.vehicles[0]?.id || null);
    } catch (e) { setData({ vehicles: [], items: [], error: formatApiError(e) }); }
  };
  useEffect(() => { load(); }, []);
  const v = useMemo(() => data?.vehicles.find(x => x.id === vid), [data, vid]);
  if (!data) return null;
  return (
    <div id="controllo" className="scroll-mt-24 rounded-2xl border border-amber-500/25 bg-gradient-to-b from-[#0F0D09] to-[#070605] p-4 sm:p-5 space-y-4" data-testid="driver-vehicle-check">
      <div className="flex items-center gap-2"><ClipboardCheck className="text-amber-400" size={20} /><h3 className="text-white font-display font-bold text-xl">Controllo veicolo</h3></div>
      {data.vehicles.length === 0 ? (
        <div className="text-sm text-slate-400" data-testid="vc-no-vehicles">Nessun veicolo assegnato a te oggi (turni, servizi o scheda veicolo). Contatta l'ufficio se devi controllare un mezzo.</div>
      ) : (<>
        <div className="flex gap-2 overflow-x-auto pb-1">
          {data.vehicles.map(x => (
            <button key={x.id} type="button" onClick={() => setVid(x.id)} data-testid={`vc-vehicle-${x.id}`}
              className={`shrink-0 flex items-center gap-2 rounded-xl border px-3 py-2 text-left ${x.id === vid ? "border-amber-400 bg-amber-500/10" : "border-slate-800"}`}>
              <Car size={16} className="text-amber-400" /><div><div className="text-sm text-white font-semibold">{x.name}</div><div className="text-[11px] text-slate-400 font-mono-num">{x.plate}</div></div>
              {x.blocked && <ShieldAlert size={14} className="text-rose-400" />}
            </button>
          ))}
        </div>
        {v && <>
          <VehicleStatus v={v} today={data.today} />
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <button type="button" onClick={() => setDialog("start")} data-testid="vc-start-btn" className="min-h-[52px] rounded-xl font-bold text-black bg-gradient-to-b from-[#F1D27A] to-[#C99A24] flex items-center justify-center gap-2"><CheckCircle2 size={18} />Inizio turno</button>
            <button type="button" onClick={() => setDialog("end")} data-testid="vc-end-btn" className="min-h-[52px] rounded-xl font-bold border border-amber-500/50 text-amber-200 flex items-center justify-center gap-2"><Flag size={18} />Fine turno</button>
            <button type="button" onClick={() => setDialog("report")} data-testid="vc-report-btn" className="min-h-[52px] rounded-xl font-bold border border-rose-500/50 text-rose-200 flex items-center justify-center gap-2"><AlertTriangle size={18} />Segnala anomalia</button>
          </div>
        </>}
      </>)}
      {checks.length > 0 && (
        <div>
          <div className="text-xs uppercase tracking-widest text-amber-400/80 mb-1">I tuoi ultimi controlli</div>
          {checks.slice(0, 5).map(c => (
            <div key={c.id} className="flex items-center justify-between text-sm border-b border-slate-800/70 py-2" data-testid={`vc-my-check-${c.id}`}>
              <span className="text-slate-300">{fmtDT(c.created_at)} · {c.kind === "start" ? "Inizio" : "Fine"} · {c.vehicle_plate}</span>
              <span className={c.anomalies ? "text-amber-300" : c.unchecked ? "text-sky-300" : "text-emerald-300"}>{c.anomalies ? `${c.anomalies} ${c.anomalies === 1 ? "anomalia" : "anomalie"}` : c.unchecked ? `Incompleto · ${c.unchecked} non verificati` : "Tutto OK"}{c.differences?.length ? ` · ${c.differences.length} differenze` : ""}</span>
            </div>
          ))}
        </div>
      )}
      {reports.length > 0 && (
        <div>
          <div className="text-xs uppercase tracking-widest text-amber-400/80 mb-1">Le tue segnalazioni</div>
          {reports.slice(0, 5).map(r => (
            <div key={r.id} className="flex items-center justify-between gap-2 text-sm border-b border-slate-800/70 py-2" data-testid={`vc-my-report-${r.id}`}>
              <span className="text-slate-300 truncate">{r.vehicle_plate} · {CATEGORY_LABEL[r.category]} · {r.description}</span>
              <span className={`shrink-0 text-[10px] uppercase tracking-wider border rounded-full px-2 py-0.5 ${REPORT_STATUS[r.status].cls}`}>{REPORT_STATUS[r.status].label}</span>
            </div>
          ))}
        </div>
      )}
      {dialog && v && dialog !== "report" && <CheckDialog vehicle={v} kind={dialog} items={data.items} onClose={() => setDialog(null)} onDone={() => { setDialog(null); load(); window.dispatchEvent(new Event("gps-refresh")); }} />}
      {dialog === "report" && v && <ReportDialog vehicle={v} onClose={() => setDialog(null)} onDone={() => { setDialog(null); load(); }} />}
    </div>
  );
}
